// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: turn a raw user agent into the few words people recognise ("iPhone", "Safari",
// "iOS 26.6"), for the push device card in Settings > Notifications.

export interface UserAgentSummary {
	device: string | null;
	browser: string | null;
	os: string | null;
}

function version(raw: string | undefined, parts = 2): string | null {
	if (!raw) return null;
	return raw.replace(/_/g, '.').split('.').slice(0, parts).join('.');
}

function detectBrowser(ua: string): string | null {
	if (/EdgA?\/|EdgiOS\//.test(ua)) return 'Edge';
	if (/OPR\/|OPiOS\//.test(ua)) return 'Opera';
	if (/SamsungBrowser\//.test(ua)) return 'Samsung Internet';
	if (/Firefox\/|FxiOS\//.test(ua)) return 'Firefox';
	if (/Chrome\/|CriOS\//.test(ua)) return 'Chrome';
	if (/Version\/[\d.]+.*Safari\//.test(ua)) return 'Safari';
	return null;
}

export function summariseUserAgent(ua: string | null | undefined): UserAgentSummary {
	if (!ua) return {device: null, browser: null, os: null};
	const browser = detectBrowser(ua);
	const apple = ua.match(/\((iPhone|iPad|iPod)[^)]*?OS ([\d_]+)/);
	if (apple) {
		// iOS 26 freezes the OS token at 18_7; Safari's Version/ then carries the real release.
		const safariVersion = ua.match(/Version\/(\d+)(?:\.(\d+))?/);
		const frozen = safariVersion && Number(safariVersion[1]) >= 26;
		const os = frozen ? version(`${safariVersion[1]}.${safariVersion[2] ?? '0'}`) : version(apple[2]);
		return {device: apple[1], browser, os: os ? `iOS ${os}` : 'iOS'};
	}
	const android = ua.match(/Android ([\d.]+)/);
	if (android) return {device: 'Android', browser, os: `Android ${version(android[1], 1)}`};
	if (/Macintosh|Mac OS X/.test(ua)) return {device: 'Mac', browser, os: 'macOS'};
	if (/Windows NT/.test(ua)) return {device: 'Windows', browser, os: 'Windows'};
	if (/CrOS/.test(ua)) return {device: 'Chromebook', browser, os: 'ChromeOS'};
	if (/Linux/.test(ua)) return {device: 'Linux', browser, os: 'Linux'};
	return {device: null, browser, os: null};
}

/** "e656bb84…c1d8d1" -> "e656…d8d1": enough to tell two subscriptions apart. */
export function shortSubscriptionId(id: string): string {
	return id.length > 10 ? `${id.slice(0, 4)}…${id.slice(-4)}` : id;
}
